/**
 * Fit Finder — standalone quiz page.
 * Pair with templates/page.fit-finder.liquid + assets/fit-finder.css + assets/fit-finder-page.css.
 *
 * Load as: <script src="{{ 'fit-finder-page.js' | asset_url }}" defer></script>
 *
 * This is NOT the same script as assets/fit-finder.js (the product-page
 * modal). This page has no modal — it's an inline quiz followed by a grid
 * of 4 products, each with its own size/variant list and Add to Cart
 * button, matched against the data-ff-* attributes in the template.
 */
(function () {
  function sizeFromChest(chest, sizeOrder) {
    const c = parseFloat(chest);
    if (!c) return null;
    const breakpoints = [34, 37, 40, 42, 44];
    let idx = breakpoints.findIndex((b) => c <= b);
    if (idx === -1) idx = sizeOrder.length - 1;
    return sizeOrder[Math.min(idx, sizeOrder.length - 1)];
  }

  function sizeFromWeight(weight, sizeOrder) {
    const w = parseInt(weight, 10);
    const mid = Math.floor(sizeOrder.length / 2);
    if (!w) return sizeOrder[mid];
    const breakpoints = [130, 145, 160, 185, 200];
    let idx = breakpoints.findIndex((b) => w <= b);
    if (idx === -1) idx = sizeOrder.length - 1;
    return sizeOrder[Math.min(idx, sizeOrder.length - 1)];
  }

  const SHAPE_DELTA = { narrower: -1, flatter: -1, average: 0, broader: 1, rounder: 1 };

  const RESULT_COPY = {
    slim: (s) => `A size ${s} sized down for a closer, tailored line through the body.`,
    regular: (s) => `A size ${s} true to size — clean through the body with proper sleeve and body length.`,
    relaxed: (s) => `A size ${s} sized up for a roomier, more relaxed drape.`
  };

  const DEFAULT_STATE = {
    step: 1, feet: 5, inches: 7, weight: 160, chest: '',
    chestShape: 'average', stomachShape: 'average', fit: 'regular'
  };

  class FitFinderPage {
    constructor(root) {
      this.root = root;

      // One card per recommended product, each with its own variant list.
      this.cards = Array.from(root.querySelectorAll('[data-ff-product-card]')).map((card) => {
        const script = card.querySelector('[data-ff-product-variants]');
        let variants = [];
        try {
          variants = JSON.parse((script && script.textContent) || '[]');
        } catch (e) {
          console.warn('Fit Finder: product variant JSON did not parse.', e);
        }
        return {
          card,
          variants,
          addBtn: card.querySelector('[data-ff-add-btn]'),
          note: card.querySelector('[data-ff-note]')
        };
      });

      // Size order derived from whatever sizes actually appear across the
      // recommended products, in the order first seen.
      this.sizeOrder = this.buildSizeOrder();

      this.state = Object.assign({}, DEFAULT_STATE);
      this.bind();
    }

    buildSizeOrder() {
      const seen = [];
      this.cards.forEach((c) => {
        c.variants.forEach((v) => {
          if (v.size && seen.indexOf(v.size) === -1) seen.push(v.size);
        });
      });
      return seen.length ? seen : ['S', 'M', 'M/L', 'L', 'XL'];
    }

    on(selector, event, handler) {
      const nodes = this.root.querySelectorAll(selector);
      if (!nodes.length) {
        console.warn('Fit Finder: no element matched', selector);
        return;
      }
      nodes.forEach((el) => el.addEventListener(event, handler));
    }

    bind() {
      this.root.querySelectorAll('.fit-finder-stepper').forEach((stepper) => {
        const key = stepper.dataset.stepper;
        const min = parseInt(stepper.dataset.min, 10);
        const max = parseInt(stepper.dataset.max, 10);
        const step = parseInt(stepper.dataset.step || '1', 10);
        const dec = stepper.querySelector('[data-dec]');
        const inc = stepper.querySelector('[data-inc]');
        if (dec) dec.addEventListener('click', () => {
          this.state[key] = Math.max(min, this.state[key] - step);
          this.renderStepper(stepper, this.state[key]);
        });
        if (inc) inc.addEventListener('click', () => {
          this.state[key] = Math.min(max, this.state[key] + step);
          this.renderStepper(stepper, this.state[key]);
        });
      });

      this.on('[data-chest-input]', 'input', (e) => { this.state.chest = e.target.value; });

      this.root.querySelectorAll('[data-choice-group]').forEach((group) => {
        const key = group.dataset.choiceGroup;
        group.querySelectorAll('[data-value]').forEach((btn) => {
          btn.addEventListener('click', () => {
            this.state[key] = btn.dataset.value;
            group.querySelectorAll('[data-value]').forEach((b) => b.removeAttribute('data-selected'));
            btn.setAttribute('data-selected', '');
          });
        });
      });

      this.on('[data-next]', 'click', () => this.goStep(this.state.step + 1));
      this.on('[data-back]', 'click', () => this.goStep(this.state.step - 1));
      this.on('[data-result]', 'click', () => { this.computeResult(); this.goStep(6); });
      this.on('[data-restart]', 'click', () => this.reset());

      this.cards.forEach((c) => {
        if (c.addBtn) c.addBtn.addEventListener('click', () => this.addToCart(c));
      });
    }

    renderStepper(stepper, value) {
      const out = stepper.querySelector('[data-value]');
      if (out) out.textContent = value;
    }

    goStep(n) {
      if (n < 1 || n > 6) return;
      this.state.step = n;
      this.root.querySelectorAll('.fit-finder-step').forEach((el) => {
        el.hidden = parseInt(el.dataset.step, 10) !== n;
      });
      const label = this.root.querySelector('[data-ff-step-label]');
      if (label) label.textContent = `Step ${n} of 6`;
      this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    computeResult() {
      const { chest, weight, chestShape, stomachShape, fit } = this.state;
      const sizeOrder = this.sizeOrder;
      const baseSize = sizeFromChest(chest, sizeOrder) || sizeFromWeight(weight, sizeOrder);
      let idx = sizeOrder.indexOf(baseSize);
      if (idx === -1) idx = Math.floor(sizeOrder.length / 2);

      const shapeSum = (SHAPE_DELTA[chestShape] || 0) + (SHAPE_DELTA[stomachShape] || 0);
      const netShape = shapeSum >= 2 ? 1 : shapeSum <= -2 ? -1 : 0;
      idx = Math.min(sizeOrder.length - 1, Math.max(0, idx + netShape));

      if (fit === 'slim' && idx > 0) idx -= 1;
      if (fit === 'relaxed' && idx < sizeOrder.length - 1) idx += 1;

      this.resultSize = sizeOrder[idx];
      const sizeEl = this.root.querySelector('[data-result-size]');
      const copyEl = this.root.querySelector('[data-result-copy]');
      const copyFn = RESULT_COPY[fit] || RESULT_COPY.regular;
      if (sizeEl) sizeEl.textContent = this.resultSize;
      if (copyEl) copyEl.textContent = copyFn(this.resultSize);

      this.applyToCards();
    }

    // Match the recommended size against each product's own variant list —
    // every product in the grid can have a different variant for the same size.
    applyToCards() {
      this.cards.forEach((c) => {
        const match = c.variants.find((v) => v.size === this.resultSize);
        if (!c.addBtn) return;

        if (match && match.available) {
          c.addBtn.disabled = false;
          c.addBtn.textContent = `Add to Cart — Size ${this.resultSize}`;
          c.addBtn.dataset.variantId = match.id;
          if (c.note) c.note.hidden = true;
        } else {
          c.addBtn.disabled = true;
          c.addBtn.textContent = 'Add to Cart';
          delete c.addBtn.dataset.variantId;
          if (c.note) {
            c.note.hidden = false;
            c.note.textContent = match
              ? `Size ${this.resultSize} is currently out of stock.`
              : `Size ${this.resultSize} isn't offered for this style.`;
          }
        }
      });
    }

    addToCart(c) {
      const variantId = c.addBtn.dataset.variantId;
      if (!variantId) return;

      const originalText = c.addBtn.textContent;
      c.addBtn.disabled = true;
      c.addBtn.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then((res) => {
          if (!res.ok) throw new Error('Add to cart failed');
          return res.json();
        })
        .then(() => {
          c.addBtn.textContent = 'Added ✓';
          if (c.note) {
            c.note.hidden = false;
            c.note.textContent = 'Added to your cart.';
          }
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          setTimeout(() => {
            c.addBtn.disabled = false;
            c.addBtn.textContent = originalText;
          }, 2500);
        })
        .catch((err) => {
          console.warn('Fit Finder: add to cart failed.', err);
          c.addBtn.disabled = false;
          c.addBtn.textContent = originalText;
          if (c.note) {
            c.note.hidden = false;
            c.note.textContent = 'Something went wrong adding this to your cart. Please try again.';
          }
        });
    }

    reset() {
      this.state = Object.assign({}, DEFAULT_STATE);
      const chestInput = this.root.querySelector('[data-chest-input]');
      if (chestInput) chestInput.value = '';
      this.root.querySelectorAll('.fit-finder-stepper').forEach((s) =>
        this.renderStepper(s, this.state[s.dataset.stepper])
      );
      this.root.querySelectorAll('[data-choice-group]').forEach((group) => {
        group.querySelectorAll('[data-value]').forEach((b) => b.removeAttribute('data-selected'));
        const def = group.querySelector('[data-value="average"], [data-value="regular"]');
        if (def) def.setAttribute('data-selected', '');
      });
      this.cards.forEach((c) => {
        if (!c.addBtn) return;
        c.addBtn.disabled = false;
        c.addBtn.textContent = 'Add to Cart';
        delete c.addBtn.dataset.variantId;
        if (c.note) c.note.hidden = true;
      });
      this.goStep(1);
    }
  }

  function init() {
    const root = document.querySelector('.ff-page');
    if (root) new FitFinderPage(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();